import { useTranslation } from '@/contexts/dictionaryContext';
import useGlobalContext from '@/hooks/useGlobalContext';
import { useLocale } from '@/hooks/useLocale';
import {
  translateMissingLocale,
  translatePathname,
} from '@/utils/translatePathname';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';
import { i18n } from '../../../i18n.config';

export const useRedirectFromLangChange = () => {
  const [queryParams, setQueryParams] = useState('');
  const pathname = usePathname();
  const { slugs } = useTranslation();
  const lang = useLocale();
  const { isDynamicUrl, dynamicUrl } = useGlobalContext();

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const queryString = window.location.search;
      setQueryParams(queryString);
    }
  }, []);

  function getRedirectUrl(locale: string) {
    const redirectUrl = isDynamicUrl
      ? dynamicUrl[locale]
      : redirectedPathName(locale);

    return redirectUrl;
  }

  const redirectedPathName = (locale: string) => {
    if (!pathname) return '/';

    const pathnameIsMissingLocale = i18n.locales.every(
      (locale) =>
        !pathname.startsWith(`/${locale}/`) && pathname !== `/${locale}`,
    );

    if (pathnameIsMissingLocale) {
      const translations = translateMissingLocale(
        pathname,
        locale,
        slugs,
        lang,
      );

      if (translations) {
        switch (Object.keys(translations).length) {
          case 1:
            if (
              (queryParams &&
                translations.slugOne &&
                translations.slugOne.includes('book-experience')) ||
              pathname.includes('book-villa')
            ) {
              return locale === 'en' ? '/' : `/${locale}`;
            }
            if (queryParams) {
              return translations.slugOne
                ? `/${locale}/${translations.slugOne}${queryParams}`
                : `/${locale}${queryParams}`;
            }
            return translations.slugOne
              ? `/${locale}/${translations.slugOne}`
              : `/${locale}`;
          case 2:
            if (queryParams) {
              return `/${locale}/${translations.slugOne}/${translations.slugTwo}${queryParams}`;
            }
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}`;
          case 3:
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}/${translations.slugThree}`;
          case 4:
            return `/${locale}/${translations.slugOne}/${translations.slugTwo}/${translations.slugThree}/${translations.slugFour}`;
          default:
            break;
        }
      }

      if (locale === i18n.defaultLocale) {
        if (queryParams) {
          return `${pathname}${queryParams}`;
        }
        return pathname;
      }

      if (queryParams) {
        return `/${locale}${pathname}${queryParams}`;
      }

      return `/${locale}${pathname}`;
    } else {
      const segments = pathname.split('/');
      const isHome = segments.length === 2;

      if (locale === i18n.defaultLocale && isHome) {
        return '/';
      } else if (locale !== i18n.defaultLocale && isHome) {
        segments[1] = locale;
        return segments.join('/');
      }

      const translations = translatePathname(pathname, locale, slugs, lang);

      if (
        (queryParams && translations?.includes('book-experience')) ||
        pathname.includes('book-villa')
      ) {
        return locale === 'en' ? '/' : `/${locale}`;
      }

      if (queryParams) {
        return `${translations}${queryParams}`;
      }

      return translations as string;
    }
  };

  return { getRedirectUrl };
};
